import { z } from "zod";
import { VIEW_GROUPS, DELETED_VIEW } from "./tickets";
import { CustomersSidebarSchema, isCustomerSegment, type CustomersBuiltIn, type CustomersSidebar, type CustomersSidebarItem } from "./customersSidebar";

/** The sidebar and consent round (brief docs/briefs/sidebar-and-consent-round.md, items 1 and 2 with "After the red team's review"): the inbox's
 *  views column as each person's own layout. Pure rules, shared by the API (what it stores and answers) and the web app (what it draws).
 *
 *  In plain words: the **base** is today's column (three built-in groups); a person's **record** is stored only once they change something
 *  (`member_settings.sidebar`, null until then), and holds their whole arrangement; on every read the record is laid over the base
 *  (`resolveSidebar`), so a built-in group cannot be lost by accident, and a view the product adds later still reaches everyone.
 *  The sidebar editor rework (2026-10-26, docs/briefs/sidebar-rework-round.md): every group keeps the name the person gives it, and what the
 *  record lists under `removed` stays out. */

/** what an entry of the column can be: a built-in view, a ticket tag's view, a customer tag's view (the tickets of the people who carry it) */
export const SIDEBAR_ITEM_KINDS = ["view", "tag", "customerTag"] as const;
export type SidebarItemKind = (typeof SIDEBAR_ITEM_KINDS)[number];
export const SIDEBAR_BUILT_INS = ["tickets", "team", "ai"] as const;
export type SidebarBuiltIn = (typeof SIDEBAR_BUILT_INS)[number];
/** limits: a group's name, the entries of one group, the groups of one layout (a person's column, never an endless record) */
export const SIDEBAR_LIMITS = Object.freeze({ nameMax: 40, itemsPerGroup: 200, groups: 30 });

const SidebarItemSchema = z.object({ kind: z.enum(SIDEBAR_ITEM_KINDS), id: z.string().min(1).max(100), hidden: z.boolean() }).strict();
const SidebarGroupSchema = z.object({
  id: z.string().min(1).max(100),
  name: z.string().trim().min(1).max(SIDEBAR_LIMITS.nameMax),
  builtIn: z.enum(SIDEBAR_BUILT_INS).optional(),
  hidden: z.boolean(),
  folded: z.boolean(),
  items: z.array(SidebarItemSchema).max(SIDEBAR_LIMITS.itemsPerGroup),
}).strict();
/** a removal on purpose (the sidebar editor rework, 2026-10-26): `view:<id>` for a built-in view, `group:<tickets|team|ai>` for a built-in group */
export const SIDEBAR_REMOVED_MAX = 64;
const SidebarRemovedSchema = z.string().regex(/^(view:[a-z0-9-]{1,100}|group:(tickets|team|ai))$/);
/** `member_settings.sidebar`: version 1. `removed` (the sidebar editor rework, 2026-10-26; optional, no migration): what the person took out
 *  on purpose, so the read does not put it back while a view the product adds later still reaches them. */
/** Team chat's folded headings (the queue-and-chat-column round, 2026-11-03, part two): per person, all three keys every time, true = folded.
 *  No migration: stored beside the inbox column's layout in the same record (`chatFolds` on `member_settings.sidebar`), never overwritten by
 *  an inbox save or reset and never overwriting the layout; read back as `me.chatFolds`. */
export const ChatFoldsSchema = z.object({ channels: z.boolean(), tickets: z.boolean(), dms: z.boolean() }).strict();
export type ChatFolds = z.infer<typeof ChatFoldsSchema>;
export const NO_CHAT_FOLDS: ChatFolds = { channels: false, tickets: false, dms: false };
export const SidebarSchema = z.object({
  v: z.literal(1),
  groups: z.array(SidebarGroupSchema).max(SIDEBAR_LIMITS.groups),
  removed: z.array(SidebarRemovedSchema).max(SIDEBAR_REMOVED_MAX).optional(),
  chatFolds: ChatFoldsSchema.optional(), // the queue-and-chat-column round: the server keeps it, the read's layout never carries it
}).strict();
export type SidebarItem = z.infer<typeof SidebarItemSchema>;
export type SidebarGroup = z.infer<typeof SidebarGroupSchema>;
export type Sidebar = z.infer<typeof SidebarSchema>;

/** The views the column can hold, with their words: the base's, plus the ones only the pool offers (question 7: "Your open", "Recently
 *  updated", "Recently solved"). Deleted and Unknown address show only while they hold something (question 3). `count`: the entry shows the
 *  view's number. */
export type SidebarViewMeta = { label: string; icon: string; count?: boolean; onlyWhenFilled?: boolean };
export const UNKNOWN_ADDRESS_VIEW = "unknown-address" as const;
const POOL_ONLY: Record<string, SidebarViewMeta> = {
  "your-open": { label: "Your open", icon: "circleDot" },
  "recently-updated": { label: "Recently updated", icon: "history" },
  "recently-solved": { label: "Recently solved", icon: "checkCircle" },
};
export const SIDEBAR_VIEWS: Record<string, SidebarViewMeta> = {
  ...Object.fromEntries(VIEW_GROUPS.flatMap((g) => g.views.map((v) => [v.id, { label: v.label, icon: v.icon, ...(v.showCount ? { count: true } : {}) }]))),
  [DELETED_VIEW]: { label: "Deleted", icon: "trash", count: true, onlyWhenFilled: true },
  [UNKNOWN_ADDRESS_VIEW]: { label: "Unknown address", icon: "mail", count: true, onlyWhenFilled: true },
  ...POOL_ONLY,
};
export const isSidebarView = (id: string): boolean => Object.hasOwn(SIDEBAR_VIEWS, id);

const BUILT_IN_OF: Record<string, SidebarBuiltIn> = { you: "tickets", team: "team", ai: "ai" };
export const BUILT_IN_NAME: Record<SidebarBuiltIn, string> = { tickets: "Your tickets", team: "Team", ai: "AI" }; // question 1: "Your tickets" everywhere
const view = (id: string): SidebarItem => ({ kind: "view", id, hidden: false });

/** Today's column, the base every person starts from: "Your tickets", "Team" (with Deleted and Unknown address at its end), "AI". A fresh copy. */
export function defaultSidebar(): Sidebar {
  return { v: 1, groups: VIEW_GROUPS.map((g) => {
    const builtIn = BUILT_IN_OF[g.id]!;
    const items = g.views.map((v) => view(v.id));
    if (builtIn === "team") items.push(view(DELETED_VIEW), view(UNKNOWN_ADDRESS_VIEW));
    return { id: builtIn, name: BUILT_IN_NAME[builtIn], builtIn, hidden: false, folded: false, items };
  }) };
}

/* ---------- both columns: the inbox's and the Customers screen's (the customers sidebar round, 2026-10-29) ---------- */

/** a layout of either column; the Customers one carries `area: "customers"`, the inbox's no mark (its stored rows stay as they were) */
export type AnySidebar = Sidebar | CustomersSidebar;
type LooseItem = { kind: string; id: string; hidden: boolean };
type LooseGroup = { id: string; name: string; builtIn?: string; hidden: boolean; folded: boolean; items: LooseItem[] };
type Loose = { v: 1; area?: "customers"; groups: LooseGroup[]; removed?: string[] };
const loose = (l: AnySidebar) => l as unknown as Loose;
/** whether a layout is the Customers column's (read from its mark, never guessed from the kinds) */
export const isCustomersSidebar = (l: unknown): l is CustomersSidebar => !!l && typeof l === "object" && (l as { area?: unknown }).area === "customers";

const keyOf = (i: Pick<LooseItem, "kind" | "id">) => `${i.kind}:${i.id}`;
/** the key a removal on purpose is remembered by: `view:<id>` for a view, `group:<built-in>` for a built-in group (either column),
 *  `segment:<id>` and `customerTag:<id>` in the Customers column */
export const removedKeyOfView = (id: string) => `view:${id}`;
export const removedKeyOfGroup = (builtIn: SidebarBuiltIn | CustomersBuiltIn) => `group:${builtIn}`;
export const removedKeyOf = (i: Pick<LooseItem, "kind" | "id">) => keyOf(i);

/** an entry the column can still draw: an inbox view that exists, a Customers segment that exists; the rest passes (the server drops what
 *  the person cannot see) */
const known = (customers: boolean) => (i: LooseItem) => customers ? i.kind !== "segment" || isCustomerSegment(i.id) : i.kind !== "view" || isSidebarView(i.id);

/** Each entry once (the first place wins) and only entries that exist; a second group claiming the same built-in role becomes a plain group.
 *  A built-in group keeps the person's name (2026-10-26). */
function cleanGroups(groups: LooseGroup[], customers: boolean): LooseGroup[] {
  const seen = new Set<string>(); const roles = new Set<string>(); const exists = known(customers);
  return groups.map((g) => {
    const items = g.items.filter((i) => { const k = keyOf(i); if (seen.has(k) || !exists(i)) return false; seen.add(k); return true; });
    if (!g.builtIn) return { ...g, items };
    if (roles.has(g.builtIn)) { const { builtIn: _drop, ...plain } = g; return { ...plain, items }; }
    roles.add(g.builtIn);
    return { ...g, items };
  });
}

/** A built-in group the record lost without removing it on purpose comes back: after the built-in group before it in the base's order, or
 *  first when none is there. */
function restoreBuiltIns(groups: LooseGroup[], base: Loose, removed: ReadonlySet<string>): LooseGroup[] {
  const out = [...groups];
  let after = -1;
  for (const b of base.groups) {
    const at = out.findIndex((g) => g.builtIn === b.builtIn);
    if (at >= 0) { after = at; continue; }
    if (b.builtIn && removed.has(`group:${b.builtIn}`)) continue;
    out.splice(after + 1, 0, { ...b, items: [] }); after += 1;
  }
  return out;
}

/** An entry the base holds that the record does not know (and did not remove on purpose) joins its built-in group at its end, shown: a new
 *  built-in view reaches everyone, and in the Customers column a new customer tag joins the person's "Tags" group. */
function addMissing(groups: LooseGroup[], base: Loose, removed: ReadonlySet<string>): LooseGroup[] {
  const placed = new Set(groups.flatMap((g) => g.items.map(keyOf)));
  return groups.map((g) => {
    const from = g.builtIn ? base.groups.find((b) => b.builtIn === g.builtIn) : undefined;
    const missing = (from?.items ?? []).filter((i) => !placed.has(keyOf(i)) && !removed.has(keyOf(i)));
    return missing.length ? { ...g, items: [...g.items, ...missing.map((i) => ({ ...i }))] } : g;
  });
}

/** the removals still meaningful for a column: keys of what is not placed again (an entry placed again wins over its old removal) */
function liveRemovals(groups: LooseGroup[], removed: readonly string[]): string[] {
  const present = new Set(groups.flatMap((g) => [...g.items.map(keyOf), ...(g.builtIn ? [`group:${g.builtIn}`] : [])]));
  return [...new Set(removed)].filter((k) => !present.has(k));
}

/** What the column shows, read from what is stored: no record (or an unreadable one, or one of the other column) gives the base; a record
 *  keeps its order, its own groups, every group's name, what it hides and folds; a built-in group or entry missing without being removed on
 *  purpose comes back; new built-in entries appear; the removals still meaningful are handed back (so the next save carries them on). Tags
 *  and projects pass through (the server drops the ones the person cannot see). The base says which column: the inbox's by default, the
 *  Customers one when it carries the mark (`defaultCustomersSidebar(tags)`). */
export function resolveSidebar(stored: unknown, base?: Sidebar): Sidebar;
export function resolveSidebar(stored: unknown, base: CustomersSidebar): CustomersSidebar;
export function resolveSidebar(stored: unknown, base?: AnySidebar): AnySidebar;
export function resolveSidebar(stored: unknown, base: AnySidebar = defaultSidebar()): AnySidebar {
  const customers = isCustomersSidebar(base);
  const parsed = (customers ? CustomersSidebarSchema : SidebarSchema).safeParse(stored);
  if (!parsed.success) return structuredClone(base);
  const record = parsed.data as unknown as Loose;
  const cleaned = cleanGroups(record.groups, customers);
  const removedList = liveRemovals(cleaned, record.removed ?? []);
  const removed = new Set(removedList);
  const groups = addMissing(restoreBuiltIns(cleaned, loose(base), removed), loose(base), removed);
  const out: Loose = { v: 1, ...(customers ? { area: "customers" as const } : {}), groups, ...(removedList.length ? { removed: removedList } : {}) };
  return out as unknown as AnySidebar;
}

/* ---------- the tag changes the layouts follow (merge, delete, bulk delete and its Undo), in both columns ---------- */

export type SidebarPlace = { groupId: string; index: number; item: SidebarItem | CustomersSidebarItem };
/** a tag's entry: in the inbox's column a ticket or a customer tag (anything not a view); in the Customers column a customer tag only, never a
 *  segment or a project (finding 1) */
const isTagKind = (l: Loose) => (kind: string) => (l.area === "customers" ? kind === "customerTag" : kind === "tag" || kind === "customerTag");
const isTagEntry = (l: Loose, i: LooseItem, tagId: string) => isTagKind(l)(i.kind) && i.id === tagId;
/** the layout without the removal keys of these tags (a deleted or merged tag is gone for good: its key would only eat the budget, finding 5) */
function withoutTagKeys(l: Loose, tagIds: readonly string[]): Loose {
  if (!l.removed) return l;
  const removed = l.removed.filter((k) => !tagIds.some((id) => k === `customerTag:${id}`));
  const { removed: _old, ...rest } = l;
  return removed.length ? { ...rest, removed } : rest;
}

/** where a tag's entry sits in a layout (its group and position), or null */
export function placeOfTag(layout: AnySidebar, tagId: string): SidebarPlace | null {
  const l = loose(layout);
  for (const g of l.groups) { const index = g.items.findIndex((i) => isTagEntry(l, i, tagId)); if (index >= 0) return { groupId: g.id, index, item: g.items[index] as SidebarPlace["item"] }; }
  return null;
}
/** whether a layout names the tag at all: an entry, or (the Customers column) a removal on purpose */
export const mentionsTag = (layout: AnySidebar, tagId: string): boolean => !!placeOfTag(layout, tagId) || (loose(layout).removed ?? []).includes(`customerTag:${tagId}`);
/** every entry of these tags taken out, and their removal keys */
export function withoutTags<L extends AnySidebar>(layout: L, tagIds: readonly string[]): L {
  const l = loose(layout);
  const out = { ...l, groups: l.groups.map((g) => ({ ...g, items: g.items.filter((i) => !tagIds.some((id) => isTagEntry(l, i, id))) })) };
  return withoutTagKeys(out, tagIds) as unknown as L;
}
/** a merge: the old tag's entry now points at the remaining one, in its place; where the remaining one is already listed, the old entry goes.
 *  In the Customers column (question 7) the remaining tag listed only in the built-in "Tags" group moves into the old one's place, once,
 *  when the old one sat in another group: the person's own placement wins over the automatic one. */
export function replaceTag<L extends AnySidebar>(layout: L, from: string, into: string): L {
  const l = loose(layout);
  const intoAt = placeOfTag(layout, into); const fromAt = placeOfTag(layout, from);
  const tagsGroup = l.area === "customers" ? l.groups.find((g) => g.builtIn === "tags")?.id : undefined;
  const takeOver = !!intoAt && !!fromAt && !!tagsGroup && intoAt.groupId === tagsGroup && fromAt.groupId !== tagsGroup;
  const has = !!intoAt && !takeOver;
  const groups = l.groups.map((g) => ({ ...g, items: g.items.flatMap((i) => {
    if (isTagEntry(l, i, from)) return has ? [] : [{ ...i, id: into }];
    if (takeOver && g.id === tagsGroup && isTagEntry(l, i, into)) return [];
    return [i];
  }) }));
  return withoutTagKeys({ ...l, groups }, [from]) as unknown as L;
}
/** an Undo: the entry back in its group at its place (the group's end when it got shorter; nothing when the group is gone or it is listed already) */
export function putTagBack<L extends AnySidebar>(layout: L, place: SidebarPlace): L {
  if (placeOfTag(layout, place.item.id)) return layout;
  const l = loose(layout);
  return { ...l, groups: l.groups.map((g) => {
    if (g.id !== place.groupId) return g;
    const items = [...g.items]; items.splice(Math.min(place.index, items.length), 0, { ...place.item });
    return { ...g, items };
  }) } as unknown as L;
}
/** the tag ids a layout lists (the inbox: ticket and customer tags; the Customers column: customer tags) */
export const tagIdsOf = (layout: AnySidebar): string[] => { const l = loose(layout); const tag = isTagKind(l); return l.groups.flatMap((g) => g.items.filter((i) => tag(i.kind)).map((i) => i.id)); };
/** the entries of one kind the column shows (in a shown group, folded or not; not hidden): the ones whose numbers are loaded */
export const shownEntriesOf = (layout: AnySidebar, kind: string): LooseItem[] => loose(layout).groups.filter((g) => !g.hidden).flatMap((g) => g.items.filter((i) => i.kind === kind && !i.hidden));
/** the tag entries the column shows (in a shown group, folded or not; not hidden): the ones whose numbers are loaded */
export const shownTagEntries = (layout: AnySidebar): SidebarItem[] => { const l = loose(layout); const tag = isTagKind(l); return l.groups.filter((g) => !g.hidden).flatMap((g) => g.items.filter((i) => tag(i.kind) && !i.hidden)) as SidebarItem[]; };
